import type { ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'ghost' | 'danger' | 'outline'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  loading?: boolean
}

/* Icewall buttons are tinted, not solid: bg color/20, border color/60, text color */
const styles: Record<Variant, string> = {
  primary: 'border-primary/60 bg-primary/20 text-primary hover:bg-primary/5 disabled:opacity-50',
  ghost: 'border-transparent bg-transparent text-foreground hover:bg-foreground/5',
  danger: 'border-danger/60 bg-danger/20 text-danger hover:bg-danger/5 disabled:opacity-50',
  outline: 'border-foreground/20 bg-background text-foreground hover:bg-foreground/5',
}

export default function Button({
  variant = 'primary',
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: Props) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 text-sm font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/10 focus-visible:ring-offset-2 disabled:cursor-not-allowed',
        styles[variant],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  )
}
